import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
  CarouselNextButton,
  CarouselPrevButton,
} from "@aleksundshantu.com/aunds-utils-react/primitives/carousel";
import { Headline } from "@/components/Global/Headline/Headline";
import { MediaItem } from "@/components/Global/MediaItem/MediaItem";
import { Section } from "@/components/Global/Section/Section";
import { SingleImageSliderOptionsType } from "@/types/ModuleOptionsType";
import { Modul_Einzelbild_Slider } from "@/wp/types/acf-module-types";
import styles from "./SingleImageSlider.module.scss";
import { columnSize } from "@/utils/layout/columnSize";
import { useEffect, useRef, useState } from "react";
import { BottomNavigation } from "./BottomNavigation/BottomNavigation";
import { ImageModal } from "./ImageModal/ImageModal";

interface SingleImageSliderProps {
  data: Modul_Einzelbild_Slider;
  options: SingleImageSliderOptionsType;
}

export const SingleImageSlider = ({
  data,
  options,
}: SingleImageSliderProps) => {
  const { media_format, slider_options, box, columns } = options;

  const { show_card_box, show_border_radius } = box;

  const { headline, headline_html_tag, overline, subline, elements } =
    data.single_image_slider;

  const {
    background_color_class,
    id,
    section_margin_class,
    section_padding_class,
    headline_text_align_class,
    image_keep_ratio,
  } = data.layout;

  const [imageCaptionHeight, setImageCaptionHeight] = useState(0);
  const captionRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const Columns = columnSize(columns);

  useEffect(() => {
    const heights = captionRefs.current.map((el) => {
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const styles = window.getComputedStyle(el);
      const marginTop = parseFloat(styles.marginTop);
      const marginBottom = parseFloat(styles.marginBottom);
      return rect.height + marginTop + marginBottom;
    });

    const maxHeight = Math.max(...heights);
    if (maxHeight > imageCaptionHeight) {
      setImageCaptionHeight(maxHeight);
    }
  }, [captionRefs, imageCaptionHeight]);

  return (
    <Section
      id={id}
      columnsClassName="is-centered is-multiline"
      className={`${styles["single-image-slider"]} is-centered ${background_color_class} ${section_margin_class} ${section_padding_class}`}
    >
      <div className={"column is-12"}>
        {headline && (
          <Headline
            headline_html_tag={headline_html_tag}
            headline={headline}
            extraClass={headline_text_align_class}
            overline={overline}
            subline={subline}
          />
        )}
      </div>

      <div className={`column ${Columns} is-relative`}>
        <Carousel
          autoplay={slider_options.has_autoplay}
          loop={slider_options.has_looping}
        >
          <div className={styles["slider-wrapper"]}>
            <CarouselContent
              spacing="0px"
              extraClasses={`${styles["carousel-content"]} `}
            >
              {(elements ? elements : []).map(({ image }, index) => (
                <CarouselItem
                  key={index}
                  itemWidth={"100%"}
                  className={`${styles["carousel-item"]}`}
                >
                  <div
                    className={`${styles["carousel-item-content"]} columns is-flex-direction-column is-centered is-gapless  m-0 ${show_card_box ? "is-box" : ""} ${show_border_radius && show_card_box ? "has-border-radius" : ""} ${show_card_box && image_keep_ratio ? "p-3" : ""}`}
                  >
                    {image && (
                      <MediaItem
                        image_desktop={image}
                        sizes="(max-width: 1023px) 80vw, (min-width: 1407px) 60vw, 50vw"
                        priority={true}
                        mediaFormat={
                          image_keep_ratio ? undefined : media_format
                        }
                        objectFit={image_keep_ratio ? "contain" : "cover"}
                        showModal={false}
                      />
                    )}

                    {slider_options.has_modal_view && (
                      <ImageModal elements={elements || []} />
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            {slider_options.slider_navigation == "only-navigation" ? (
              <div
                role="navigation"
                className={`${styles["embla-navigation"]}`}
                style={{
                  ["--y-offset" as string]: `${imageCaptionHeight}px`,
                }}
              >
                <CarouselPrevButton />
                <CarouselNextButton />
              </div>
            ) : (
              <CarouselNavigation type={slider_options.slider_navigation} />
            )}
          </div>

          <BottomNavigation
            totalSnaps={(elements || []).length}
            hideFrom="desktop"
          />
        </Carousel>
      </div>
    </Section>
  );
};
